import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import useTableSchema from "../../../hooks/useTableSchema";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import EditAction from "../../common/edit-action/EditAction";
import { useUrl } from "../../../hooks/urlEncoding";

import {
  PartnerListColumnFilterable,
  PartnerListColumnSortable,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import DeleteAction from "../../common/delete-action/DeleteAction";
import { dateFormatter } from "../../../utils/date-formatter";
import { routerPaths } from "../../../constants/Paths";
import { useLocation } from "react-router-dom";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../hooks/useUrlSort";
import { BlockNumbersListRequest } from "../../../services/Endpoints/block-numbers/BlockNumbersListEndpoint";
import UserLink from "../common/UserLink";
import { IBlockNumberSchema } from "../../../services/Schemas/BlockNumber";
import { BlockNumbersDeleteRequest } from "../../../services/Endpoints/block-numbers/BlockNumbersDeleteEndpoint";

export interface IBlockNumber extends IBlockNumberSchema {
  operations: ReactNode;
}

export interface BlockNumbersTableProps {
  reload: boolean;
  setReload: (payload: boolean) => void;
  setBlockNumber?: (payload: Partial<IBlockNumberSchema>) => void;
  setOpenModal: (payload: boolean) => void;
}

export default function BlockNumbersTable({
  reload,
  setReload,
  setBlockNumber,
  setOpenModal,
}: Readonly<BlockNumbersTableProps>) {
  const { parseQueryString, queryStringUrl, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<IBlockNumber[] | []>([]);
  const { can } = useCanAccess();

  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const handleView = (payload: Partial<IBlockNumberSchema>) => {
    setOpenModal(true);
    setBlockNumber && setBlockNumber(payload);
  };

  const handleDelete = async (id: number) => {
    try {
      setWaiting(true);
      const ListRequest = new BlockNumbersDeleteRequest(protectedInstance);
      await ListRequest.remove(id);
      setReload(!reload);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const listRequest = new BlockNumbersListRequest(protectedInstance);
      const resp = await listRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const data = resp.data.block_numbers.map(
        ({
          group,
          description,
          number,
          partner_id,
          created_at,
          id,
          updated_at,
        }) => ({
          id,
          created_at,
          number,
          group,
          updated_at,
          description: (
            <span
              title={description}
              style={{
                width: "200px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {description}
            </span>
          ),
          partner_id: partner_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminPartnerList)}
              user={{ id: partner_id }}
              link={queryStringUrl(routerPaths.PARTNERS, {
                filters: {
                  [PartnerListColumnFilterable.ID]: {
                    column: PartnerListColumnFilterable.ID,
                    value: partner_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [PartnerListColumnSortable.ID]: {
                    column: PartnerListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          operations: (
            <Stack justifyContent="center" alignItems="center" direction="row">
              <DeleteAction
                disabled={!can(AccessEnums.AdminBlockNumbersDelete)}
                handleDelete={() => handleDelete(id)}
              />
              <EditAction
                disabled={!can(AccessEnums.AdminBlockNumbersUpdate)}
                handleEdit={() =>
                  handleView({
                    id,
                    description,
                    group,
                    number,
                  })
                }
              />
            </Stack>
          ),
        })
      );
      setData(data as any);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [reload, page, rowsPerPage, location.search]);

  const partnersColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "number",
      label: "شماره",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "group",
      label: "گروه",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "description",
      label: "توضیحات",
      minWidth: 200,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "partner_id",
      label: "شناسه شریک",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "updated_at",
      label: "تاریخ ویرایش",
      minWidth: 170,
      align: "center",
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
      FilterComponent: DateTimeFilter,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 170,
      align: "center",
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
      FilterComponent: DateTimeFilter,
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(partnersColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setReload(!reload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
